import { type MouseEvent, Fragment, forwardRef } from 'react';
import { Dayjs } from 'dayjs';

// Models
import { ICurrentAccount } from 'app/models/Account';

// Redux
import { useAppSelector } from 'app/hooks/useStore';
import { selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';

// MUI X v5
import {
  DesktopTimePicker as MuiDesktopTimePicker,
  DesktopTimePickerProps as MuiDesktopTimePickerProps,
} from '@mui/x-date-pickers/DesktopTimePicker';

// MUI
import { IconButton, TextField, TextFieldProps } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

// Utilities
import { generateAttrForTesting } from 'app/utilities/Utilities';

type Props = {
  name: string;
  slotProps?: {
    textField?: TextFieldProps;
  };
} & Omit<MuiDesktopTimePickerProps<Dayjs, Dayjs>, 'renderInput'>;

const DesktopTimePicker = forwardRef<HTMLDivElement, Props>(
  ({ name, slotProps, ...restProps }, ref) => {
    const currentAccount: ICurrentAccount | null =
      useAppSelector(selectCurrentAccount);

    // Handle the clear action
    const handleClear = (event:MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation(); // Prevent the picker from opening
      if (restProps.onChange) {
        restProps.onChange(null, ''); // Reset the value
      }
    };

    const inputFormat = currentAccount?.settings?.timeFormat || 'hh:mm a';
    const is12HourFormat = inputFormat.includes('a') || inputFormat.includes('A');

    return (
      <MuiDesktopTimePicker
        {...restProps}
        ref={ref}
        ampmInClock
        ampm={is12HourFormat}
        inputFormat={inputFormat}
        showToolbar={false}
        componentsProps={{
          actionBar: {
            actions: ['clear', 'accept'],
          },
          ...(restProps.componentsProps || {}),
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...slotProps?.textField}
            {...generateAttrForTesting('control', 'input', name)}
            name={name}
            fullWidth
            slotProps={{
              ...slotProps?.textField?.slotProps,
              input: {
                // ...params.InputProps, // Carry over the calendar icon from the picker
                ...slotProps?.textField?.slotProps?.input, // Merge custom input props
                endAdornment: (
                  <Fragment>
                    {/* Show clear button only if there is a value */}
                    {restProps.value && (
                      <IconButton
                        sx={{ mr: -2 }}
                        aria-label="clear time"
                        onClick={handleClear}
                        size="small"
                      ><ClearIcon fontSize="small" /></IconButton>
                    )}
                    {params.InputProps?.endAdornment}
                  </Fragment>
                )
              }
            }}
            margin={slotProps?.textField?.margin ?? 'normal'}
          />
        )}
      />
    );
  }
);

export default DesktopTimePicker;
